import React from 'react';
import { motion } from 'framer-motion';
import { Cookie, Settings, Shield, Trash2, Info, Mail, AlertTriangle, BarChart3 } from 'lucide-react';
import PageHeader from '../../components/UI/PageHeader';
import Container from '../../components/UI/Container';
import SEOHead from '../../components/SEO/SEOHead';
import { useSystemSettings } from '../../contexts/SystemSettingsContext';

const CookiePolicy: React.FC = () => {
  const { settings } = useSystemSettings();

  const sections = [
    {
      icon: <Info className="w-6 h-6" />,
      title: '1. Politica de utilizare cookie-uri',
      content: `Această politică se referă la cookie-urile și tehnologiile similare utilizate de site-ul Protecția Animalelor Sector 1, operat în cadrul Sectorului 1 al Municipiului București.

Politica este publicată pentru informarea utilizatorilor cu privire la modul în care pot fi folosite fișierele cookie, stocarea locală și preferințele de confidențialitate.`
    },
    {
      icon: <Cookie className="w-6 h-6" />,
      title: '2. Ce sunt cookie-urile',
      content: `Cookie-ul este un fișier de mici dimensiuni, format de regulă din litere și numere, stocat pe computerul, telefonul sau alt echipament de pe care utilizatorul accesează internetul. Cookie-ul este instalat la solicitarea unui server web către browser și este pasiv: nu conține programe software, viruși sau spyware și nu poate accesa informațiile de pe dispozitivul utilizatorului.`
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: '3. La ce sunt folosite cookie-urile',
      content: `Cookie-urile pot permite recunoașterea terminalului utilizatorului, afișarea conținutului într-un mod adaptat preferințelor și îmbunătățirea experienței de navigare.

Acestea pot fi folosite și pentru statistici anonime agregate, atunci când sunt implementate instrumente de analiză, pentru a înțelege modul de utilizare a paginilor și pentru a îmbunătăți structura și conținutul site-ului, fără identificarea directă a utilizatorilor.`
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: '4. Ce cookie-uri folosim',
      content: `Site-urile pot utiliza cookie-uri de sesiune, care rămân active până la închiderea browserului, și cookie-uri persistente, care rămân pe dispozitiv pentru o perioadă definită sau până când sunt șterse manual de utilizator.

În forma actuală, interfața publică a acestui site folosește stocare locală pentru preferințe strict funcționale, cum ar fi acceptarea informării privind cookie-urile și setările modulului de accesibilitate. Nu sunt implementate cookie-uri de marketing prin codul frontend.`
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: '5. Cum sunt folosite cookie-urile de către acest site',
      content: `O vizită pe acest site poate implica tehnologii locale în următoarele scopuri:
• funcționarea tehnică a site-ului;
• memorarea acceptării informării privind cookie-urile;
• memorarea preferințelor de accesibilitate;
• îmbunătățirea experienței de navigare.

Lista actuală a tehnologiilor folosite:
• cookie-consent-v1: stocare locală pentru reținerea acceptării informării privind cookie-urile;
• accessibility-preferences: stocare locală pentru preferințele modulului de accesibilitate.

În cazul în care vor fi adăugate ulterior servicii de analiză, conținut terț sau marketing, politica va fi actualizată și va fi implementat mecanismul de consimțământ necesar.`
    },
    {
      icon: <Info className="w-6 h-6" />,
      title: '6. Conțin cookie-urile date personale?',
      content: `Cookie-urile și datele locale nu solicită, prin ele însele, informații cu caracter personal. În cele mai multe situații, acestea nu identifică direct utilizatorii de internet.

Dacă date personale ar fi colectate prin intermediul unor funcționalități ale site-ului, acestea ar fi prelucrate numai pentru scopurile declarate și cu respectarea Regulamentului (UE) 2016/679 și a legislației naționale aplicabile.`
    },
    {
      icon: <Trash2 className="w-6 h-6" />,
      title: '7. Ștergerea cookie-urilor',
      content: `Browserul permite, de regulă, salvarea cookie-urilor și a datelor locale în mod implicit. Aceste setări pot fi modificate astfel încât salvarea să fie blocată, utilizatorul să fie informat de fiecare dată când se salvează date noi sau datele existente să fie șterse.

Limitarea cookie-urilor sau a stocării locale poate afecta preferințele salvate, dar nu ar trebui să împiedice accesarea conținutului public al site-ului.`
    },
    {
      icon: <Cookie className="w-6 h-6" />,
      title: '8. De ce sunt importante cookie-urile',
      content: `Cookie-urile contribuie la funcționarea eficientă a internetului și la generarea unei experiențe de navigare adaptate preferințelor utilizatorului. Refuzarea sau dezactivarea lor poate face anumite funcții mai greu de utilizat sau indisponibile.

În cazul acestui site, preferințele locale ajută în special la reținerea setărilor alese de utilizator și la evitarea afișării repetate a aceleiași informări.`
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: '9. Securitate și confidențialitate',
      content: `Cookie-urile nu sunt viruși și nu pot rula cod. Totuși, informațiile salvate în browser trebuie administrate responsabil, mai ales pe dispozitive partajate sau rețele nesecurizate.

Pentru o navigare sigură, recomandăm actualizarea browserului, verificarea periodică a setărilor de confidențialitate și ștergerea datelor locale atunci când utilizați un dispozitiv la care au acces și alte persoane.`
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: '10. Linkuri utile',
      content: `Pentru configurarea cookie-urilor, consultați secțiunea de ajutor sau preferințe a browserului folosit. Informații suplimentare despre publicitatea comportamentală și opțiunile utilizatorilor pot fi consultate pe www.youronlinechoices.com/ro/.`
    }
  ];

  return (
    <>
      <SEOHead
        title="Politica privind cookie-urile"
        description="Politica privind cookie-urile pentru site-ul Protecția Animalelor Sector 1. Informații despre cookie-uri, stocare locală, preferințe și administrarea acestora."
        keywords="politica cookie, cookie-uri, GDPR, Legea 506/2004, protecția datelor, confidențialitate"
        url="/politica-cookie"
        type="website"
        noindex={true}
      />
      <PageHeader
        title="Politica privind cookie-urile"
        subtitle="Informații despre cookie-uri, tehnologii similare și preferințe locale"
      />

      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-blue-50 border-l-4 border-primary p-6 mb-8 rounded-r-lg">
            <div className="flex items-center mb-3">
              <Cookie className="w-6 h-6 text-primary mr-3" />
              <h2 className="text-lg font-bold text-primary">Ultima actualizare</h2>
            </div>
            <p className="text-gray-700">
              Această politică a fost actualizată la data de <strong>20 mai 2026</strong> și este structurată după modelul publicat de Primăria Sectorului 1 pentru informarea utilizatorilor în legătură cu Legea nr. 506/2004 și Regulamentul (UE) 2016/679.
            </p>
          </div>

          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.section
                key={section.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-md p-6"
              >
                <div className="flex items-start">
                  <div className="bg-primary-100 p-3 rounded-full mr-4 flex-shrink-0">
                    <div className="text-primary">
                      {section.icon}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-primary mb-3 font-display">
                      {section.title}
                    </h2>
                    <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {section.content}
                    </div>
                  </div>
                </div>
              </motion.section>
            ))}
          </div>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="bg-white rounded-lg shadow-md p-6 mt-8"
          >
            <h2 className="text-xl font-bold text-primary mb-4 font-display flex items-center">
              <Mail className="w-5 h-5 mr-2" />
              Contact
            </h2>
            <p className="text-gray-700 mb-4">
              Pentru întrebări privind cookie-urile, tehnologiile similare sau datele personale, ne puteți contacta folosind datele de mai jos.
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>Email:</strong> {settings?.email || 'protectiaanimalelor@primarias1.ro'}</p>
              <p><strong>Telefon:</strong> {settings?.phone || '+40 750 209 751'}</p>
              <p><strong>Adresa:</strong> {settings?.address || 'Sos. Odăi, nr. 3-5, Sector 1, București'}</p>
            </div>
          </motion.section>
        </motion.div>
      </Container>
    </>
  );
};

export default CookiePolicy;
